import { Loader2, Mail, Pencil, Power } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { UpdateAdminUserFormValues } from '../schemas/admin-user-schema';
import {
  getAdminUserStatus,
  type AdminUserDetailDto,
  type AdminUserListDto,
} from '../types/admin-user.types';
import { useAuthorization } from '@/core/hooks/useAuthorization';
import { useAuthStore } from '@/core/stores/authStore';
import { Form, FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';
import { formatAdminDateTime } from '@/shared/utils/format-date.utils';
import { getRoleLabel } from '@/shared/utils/role-label.utils';

interface AdminUserInfoTabProps {
  detail: AdminUserDetailDto;
  user: AdminUserListDto;
  isEditing: boolean;
  isUpdating: boolean;
  form: UseFormReturn<UpdateAdminUserFormValues>;
  onEditSubmit: (values: UpdateAdminUserFormValues) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onOpenToggle: () => void;
  onOpenRoleDialog: () => void;
  onOpenResend: () => void;
}

export function AdminUserInfoTab({
  detail,
  user,
  isEditing,
  isUpdating,
  form,
  onEditSubmit,
  onStartEdit,
  onCancelEdit,
  onOpenToggle,
  onOpenRoleDialog,
  onOpenResend,
}: AdminUserInfoTabProps) {
  const { user: currentUser } = useAuthStore();
  const { hasPermission } = useAuthorization();

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onEditSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.23rem]">
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Nombre completo"
                    placeholder="Sin nombre"
                    {...field}
                    disabled={!isEditing || isUpdating}
                    error={form.formState.errors.displayName?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Correo electrónico"
                    type="email"
                    {...field}
                    disabled={!isEditing || isUpdating}
                    error={form.formState.errors.email?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input label="Rol" value={getRoleLabel(detail.roleName)} disabled readOnly />
            </div>
            <UnifiedButton
              type="button"
              buttonType="basic"
              variant="outline"
              onClick={onOpenRoleDialog}
              disabled={
                isEditing ||
                currentUser?.userId === user.id ||
                !hasPermission(PERMISSIONS.ROLES_CHANGE)
              }
              className="shrink-0"
              icon={<Pencil size={16} />}
              data-testid="admin-users-info-change-role-button"
            >
              Cambiar rol
            </UnifiedButton>
          </div>

          <div>
            <Input label="Estado" value={getAdminUserStatus(detail).label} disabled readOnly />
          </div>

          <div>
            <Input
              label="Fecha de creación"
              value={formatAdminDateTime(detail.createdAt, 'Nunca')}
              disabled
              readOnly
            />
          </div>

          <div>
            <Input
              label="Último acceso"
              value={formatAdminDateTime(detail.lastLoginAt, 'Nunca')}
              disabled
              readOnly
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-outline">
          {!isEditing ? (
            <>
              {hasPermission(PERMISSIONS.USERS_EDIT) && (
                <UnifiedButton
                  type="button"
                  buttonType="basic"
                  variant="outline"
                  onClick={onStartEdit}
                  icon={<Pencil size={16} />}
                  data-testid="admin-users-info-edit-button"
                >
                  Editar
                </UnifiedButton>
              )}
              {hasPermission(PERMISSIONS.USERS_TOGGLE) && (
                <UnifiedButton
                  type="button"
                  buttonType="basic"
                  variant="outline"
                  onClick={onOpenToggle}
                  icon={<Power size={16} />}
                  disabled={!user.isPasswordConfigured || currentUser?.userId === user.id}
                  data-testid="admin-users-info-toggle-status-button"
                >
                  {user.isActive ? 'Desactivar' : 'Activar'}
                </UnifiedButton>
              )}
              {hasPermission(PERMISSIONS.USERS_RESEND) && (
                <UnifiedButton
                  type="button"
                  buttonType="basic"
                  variant="outline"
                  onClick={onOpenResend}
                  icon={<Mail size={16} />}
                  disabled={detail.isPasswordConfigured}
                  data-testid="admin-users-info-resend-button"
                >
                  Reenviar invitación
                </UnifiedButton>
              )}
            </>
          ) : (
            <>
              <UnifiedButton
                type="button"
                buttonType="basic"
                variant="default"
                onClick={onCancelEdit}
                disabled={isUpdating}
                data-testid="admin-users-info-cancel-button"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                buttonType="basic"
                variant="outline"
                actionType="submit"
                disabled={isUpdating || !form.formState.isDirty}
                data-testid="admin-users-info-save-button"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar cambios'
                )}
              </UnifiedButton>
            </>
          )}
        </div>
      </form>
    </Form>
  );
}
